import { useEffect, useEffectEvent, useRef } from 'react';

/**
 * Put the signed-in user's name into a person field once it is actually KNOWN.
 *
 * The record dialogs prefill their Autor/Bearbeiter/Fotograf when they open,
 * from `useCurrentPerson`. That answers `null` until the account names have
 * arrived (see useUserLabelState) — deliberately, because the fallback in that
 * window is the Synology login name. Which leaves the other half: a dialog
 * opened in that moment would start blank and stay blank.
 *
 * So the prefill is repeated when the name turns up, ONCE per opening and only
 * while the field is still untouched — `fill` does that check. Nothing is
 * marked dirty: this is the prefill arriving late, not an edit.
 */
export function useSelfPrefill(
    /** Whether a prefill is wanted at all: dialog open, on a NEW record. */
    active: boolean,
    /** The signed-in user's name, '' while it is not known yet. */
    name: string,
    /** Writes the name if the field is still empty. */
    fill: (name: string) => void
): void {
    // An effect event so a caller may pass an inline closure without
    // re-running the effect on every render.
    const onFill = useEffectEvent((value: string) => fill(value));
    const filled = useRef(false);

    useEffect(() => {
        if (!active) {
            filled.current = false;
            return;
        }
        if (!name || filled.current) {
            return;
        }
        filled.current = true;
        onFill(name);
    }, [active, name]);
}
